import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { isAdmin } from 'src/services/auth/auth.utils';
import {
  useCreateEmailMessageTemplateMutation,
  useUpdateEmailMessageTemplateMutation,
} from 'src/services/email-sync/email-sync.service';
import {
  TemplateVisibility,
  EmailMessageTemplate,
  EntityTemplateVariables,
  CreateMessageTemplateDto,
  EmailMessageTemplateType,
} from 'src/services/email-sync/email-sync.types';

import FormProvider, {
  RHFEditor,
  RHFSelect,
  RHFTextField,
} from 'src/components/hook-form';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  template?: EmailMessageTemplate;
};

type FormValuesProps = CreateMessageTemplateDto;

export default function EmailTemplateEditor({ open, onClose, template }: Props) {

  const { t } = useTranslate();

  const authContext = useAuthContext();

  const editorRef = useRef<any>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const [focusedField, setFocusedField] = useState<'subject' | 'content' | null>(null);

  const [createTemplate, { isLoading: isCreating }] = useCreateEmailMessageTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateEmailMessageTemplateMutation();

  const isCurrentUserAdmin = isAdmin(authContext);
  const isEdit = !!template;

  const NewTemplateSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.required')),
    type: Yup.mixed<EmailMessageTemplateType>().required(t('validation.required')),
    visibility: Yup.mixed<TemplateVisibility>().required(t('validation.required')),
    subject: Yup.string().required(t('validation.required')),
    content: Yup.string().required(t('validation.required')),
  });

  const defaultValues = useMemo(
    () => ({
      name: template?.name || '',
      type: template?.type || EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      visibility: template?.visibility || TemplateVisibility.ACCOUNT_SPECIFIC,
      subject: template?.subject || '',
      content: template?.content || '',
    }),
    [template]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(NewTemplateSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const watchedType = watch('type');
  const watchedSubject = watch('subject');
  const watchedContent = watch('content');

  const availableVariables = useMemo(() => {
    if (!watchedType) return {};
    return EntityTemplateVariables[watchedType] || {};
  }, [watchedType]);

  // Function to extract variables from text
  const extractVariables = useCallback((text: string): string[] => {
    if (!text) return [];
    const variableRegex = /\{\{([^}]+)\}\}/g;
    return Array.from(text.matchAll(variableRegex), (match) => match[0]);
  }, []);

  // Validate variables in subject and content
  const variableValidation = useMemo(() => {
    const availableVarValues = Object.values(availableVariables) as string[];

    const subjectVariables = extractVariables(watchedSubject || '');
    const contentVariables = extractVariables(watchedContent || '');

    const invalidSubjectVars = subjectVariables.filter(variable =>
      !availableVarValues.includes(variable)
    ).filter((v, i, a) => a.indexOf(v) === i);

    const invalidContentVars = contentVariables.filter(variable =>
      !availableVarValues.includes(variable)
    ).filter((v, i, a) => a.indexOf(v) === i);

    return {
      subject: {
        hasInvalid: invalidSubjectVars.length > 0,
        invalidVariables: invalidSubjectVars
      },
      content: {
        hasInvalid: invalidContentVars.length > 0,
        invalidVariables: invalidContentVars
      }
    };
  }, [watchedSubject, watchedContent, availableVariables, extractVariables]);

  useEffect(() => {
    if (template) {
      reset(defaultValues);
    }
  }, [template, defaultValues, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isEdit && template) {
        await updateTemplate({
          id: template.id,
          data,
        }).unwrap();

        enqueueSnackbar(t('email_sync.templates.api.update.success'), {
          variant: 'success',
        });
      } else {
        await createTemplate(data).unwrap();

        enqueueSnackbar(t('email_sync.templates.api.create.success'), {
          variant: 'success',
        });
      }
      onClose();
      reset();
    } catch (error) {
      if (isEdit) {
        enqueueSnackbar(t('email_sync.templates.api.update.default_error'), {
          variant: 'error',
        });
      }
      else {
        enqueueSnackbar(t('email_sync.templates.api.create.default_error'), {
          variant: 'error',
        });
      }

    }
  });

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const insertVariable = useCallback((variable: string) => {
    // Determine target field based on focus state
    const targetField = focusedField || 'content'; // Default to content if no field is focused

    if (targetField === 'subject') {
      // Insert into subject field
      const currentSubject = methods.getValues('subject');
      if (subjectRef.current) {
        const input = subjectRef.current;
        const cursorPosition = input.selectionStart || currentSubject.length;
        const newSubject =
          `${currentSubject.slice(0, cursorPosition)}${variable}${currentSubject.slice(cursorPosition)}`;
        methods.setValue('subject', newSubject, { shouldValidate: true });

        // Set cursor position after the inserted variable
        setTimeout(() => {
          if (input) {
            const newPosition = cursorPosition + variable.length + 2; // +2 for spaces
            input.setSelectionRange(newPosition, newPosition);
            input.focus();
          }
        }, 0);
      } else {
        // Fallback: append to subject
        methods.setValue('subject', `${currentSubject}${variable}`, { shouldValidate: true });
      }
    } else if (targetField === 'content') {
      if (editorRef.current) {

        const editor = editorRef.current.getEditor();
        const range = editor.getSelection(true);
        if (range) {
          editor.insertText(range.index, variable);
          // Move cursor to the end of the inserted variable
          editor.setSelection(range.index + variable.length, 0);
          editor.focus();
        } else {
          // If no selection, append to the end
          editor.insertText(editor.getLength(), variable);
          editor.setSelection(editor.getLength(), 0);
          editor.focus();
        }

      }
      else {
        // Fallback: append to content
        methods.setValue('content', `${methods.getValues('content')}${variable}`, { shouldValidate: true });
      }
    }
  }, [methods, focusedField]);

  const handleSubjectFocus = useCallback(() => {
    setFocusedField('subject');
  }, []);

  const handleContentFocus = useCallback(() => {
    setFocusedField('content');
  }, []);

  const templateTypeOptions = [
    {
      value: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      label: t('email_sync.templates.template_types.candidate_engagement'),
    },
    {
      value: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      label: t('email_sync.templates.template_types.contact_engagement'),
    },
    {
      value: EmailMessageTemplateType.APPLICATION_UPDATE,
      label: t('email_sync.templates.template_types.application_update'),
    },
    {
      value: EmailMessageTemplateType.OFFER_CONTENT,
      label: t('email_sync.templates.template_types.offer_content'),
    },
    {
      value: EmailMessageTemplateType.APPLICATION_REJECTION,
      label: t('email_sync.templates.template_types.application_rejection'),
    },

  ];

  const visibilityOptions = useMemo(() => {
    const options = [
      {
        value: TemplateVisibility.ACCOUNT_SPECIFIC,
        label: t('email_sync.templates.visibility_types.account_specific'),
        disabled: false,
      }
    ];

    if (isCurrentUserAdmin) {
      options.push(
        {
          value: TemplateVisibility.ORG_WIDE,
          label: t('email_sync.templates.visibility_types.org_wide'),
          disabled: !isCurrentUserAdmin,
        });
    }

    return options;

  }, [isCurrentUserAdmin, t]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>
          {isEdit
            ? t('email_sync.templates.edit_template')
            : t('email_sync.templates.create_template')}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <RHFTextField
              name="name"
              label={t('email_sync.templates.form.name.label')}
              placeholder={t('email_sync.templates.form.name.placeholder')}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <RHFSelect
                name="type"
                disabled={isEdit}
                label={t('email_sync.templates.form.type.label')}
                placeholder={t('email_sync.templates.form.type.placeholder')}
              >
                {templateTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFSelect
                name="visibility"
                disabled={!isCurrentUserAdmin}
                label={t('email_sync.templates.form.visibility.label')}
                placeholder={t('email_sync.templates.form.visibility.placeholder')}
              >
                {visibilityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Stack>

            {Object.keys(availableVariables).length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('email_sync.templates.form.available_variables.label')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(availableVariables).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={value as string}
                      size="small"
                      variant="outlined"
                      onClick={() => insertVariable(value as string)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('email_sync.templates.form.available_variables.help')}
                </Typography>
              </Box>
            )}

            <Box>
              <RHFTextField
                name="subject"
                label={t('email_sync.templates.form.subject.label')}
                placeholder={t('email_sync.templates.form.subject.placeholder')}
                inputRef={subjectRef}
                onFocus={handleSubjectFocus}
              />
              {variableValidation.subject.hasInvalid && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {t('email_sync.templates.form.subject.unsupported_variables_warning')}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {variableValidation.subject.invalidVariables.map((variable, index) => (
                      <Chip
                        key={index}
                        label={variable}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>

            <Box>
              <RHFEditor
                name="content"
                label={t('email_sync.templates.form.content.label')}
                placeholder={t('email_sync.templates.form.content.placeholder')}
                ref={editorRef}
                onFocus={handleContentFocus}
                sx={{
                  '& .ql-editor': {
                    minHeight: 120,
                  },
                  '& .ql-container.ql-snow': {
                    borderColor: 'divider',
                  },
                  '& .ql-toolbar.ql-snow': {
                    borderColor: 'divider',
                  },
                }}
              />
              {variableValidation.content.hasInvalid && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {t('email_sync.templates.form.content.unsupported_variables_warning')}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {variableValidation.content.invalidVariables.map((variable, index) => (
                      <Chip
                        key={index}
                        label={variable}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting || isCreating || isUpdating}
          >
            {isEdit ? t('common.save') : t('common.create')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}